import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { AccountingInvoiceSyncStatusFailureCodeEnum } from 'anchor-ts-infra/src/dto/accounting-invoice/model';
import { getTestIds } from 'seagull/utils/TestIds';
import ContactSupportButton from '~/components/ContactSupportButton';
import ExternalLinkButton from '~/components/ExternalLinkButton';
import { getAccountingDetails } from '~/integrations/Accounting/accounting.utils';
import { Accounting } from '~/types/accounting';
import { helpArticles } from './helpArticles';
export const accountingSyncErrorTextsPath = 'integrations.accountingSyncErrors';
export const GetAccountingSyncErrorMessageTID = ()=>getTestIds();
const { AccountingSyncErrorMessageTestIds } = GetAccountingSyncErrorMessageTID();
const contactSupport = /*#__PURE__*/ _jsx(ContactSupportButton, {
    testId: AccountingSyncErrorMessageTestIds.ContactSupport()
});
const readMoreLink = (link)=>/*#__PURE__*/ _jsx(ExternalLinkButton, {
        openInNewTab: true,
        testId: AccountingSyncErrorMessageTestIds.ReadMore(),
        href: link,
        bi: {
            name: 'accounting_error_read_more'
        }
    });
var FailureType = /*#__PURE__*/ function(FailureType) {
    FailureType["Invoice"] = "Invoice";
    FailureType["Payment"] = "Payment";
    FailureType["Payout"] = "Payout";
    FailureType["Refund"] = "Refund";
    return FailureType;
}(FailureType || {});
function translatedFailureType(failureType, texts) {
    switch(failureType){
        case "Invoice":
            return texts.failureTypes.invoice();
        case "Payment":
            return texts.failureTypes.payment();
        case "Payout":
            return texts.failureTypes.payout();
        case "Refund":
            return texts.failureTypes.refund();
    }
}
function translatedFailureTypeLowercase(failureType, texts) {
    switch(failureType){
        case "Invoice":
            return texts.failureTypesLowercase.invoice();
        case "Payment":
            return texts.failureTypesLowercase.payment();
        case "Payout":
            return texts.failureTypesLowercase.payout();
        case "Refund":
            return texts.failureTypesLowercase.refund();
    }
}
export function getPayoutAccountingErrorMessage(param) {
    let { payoutIntegration, accounting, texts } = param;
    return payoutIntegration.accept({
        whenSyncFailed: (data)=>getFailureMessage({
                failure: data,
                failureType: "Payout",
                accounting,
                texts
            }),
        whenPendingSync: ()=>null,
        whenSyncInProgress: ()=>null,
        whenSynced: ()=>null,
        whenDeleted: ()=>null
    });
}
export function getAccountingInvoiceErrorMessage(param) {
    let { invoiceIntegration, accounting, texts } = param;
    const nullFnc = ()=>null;
    return invoiceIntegration.accept({
        whenInvoiceSyncFailed: (data)=>getFailureMessage({
                failure: data.failure,
                failureType: "Invoice",
                accounting,
                texts
            }),
        whenRefundSyncFailed: (data)=>getFailureMessage({
                failure: data.failure,
                failureType: "Refund",
                accounting,
                texts
            }),
        whenPaymentSyncFailed: (data)=>getFailureMessage({
                failure: data.failure,
                failureType: "Payment",
                accounting,
                texts
            }),
        whenPendingSync: nullFnc,
        whenInvoiceRecorded: nullFnc,
        whenVoidedRecorded: nullFnc,
        whenPaymentRecorded: nullFnc,
        whenRefundRecorded: nullFnc
    });
}
function getFailureMessage(param) {
    let { failure, failureType, accounting, texts } = param;
    var _failure_failureParam;
    const param1 = (_failure_failureParam = failure.failureParam()) !== null && _failure_failureParam !== void 0 ? _failure_failureParam : '';
    const { fullName: accountingName, helpArticle } = getAccountingDetails(accounting);
    const failureTypeText = translatedFailureType(failureType, texts);
    switch(failure.failureCode()){
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrUnknown:
            return renderUnclassifiedFailure({
                failure,
                failureType,
                accounting,
                texts
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrDuplicateDocNumber:
            return texts.duplicateDocNumber({
                failureType: failureTypeText,
                accountingName
            }, {
                readMore: readMoreLink(helpArticle.changeInvoicePrefix)
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrAmountCalculationIncorrect:
            return texts.amountCalculationIncorrect({
                failureType: failureTypeText
            }, {
                contactSupport
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrCustomerDeleted:
            if (accounting === Accounting.Xero) {
                return texts.customerArchivedXero({
                    failureType: failureTypeText,
                    accountingName,
                    param: param1
                }, {
                    readMore: readMoreLink(helpArticle.reactivateItems)
                });
            }
            return texts.customerInactive({
                failureType: failureTypeText,
                accountingName,
                param: param1
            }, {
                readMore: readMoreLink(helpArticle.reactivateItems)
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrItemDeleted:
            if (accounting === Accounting.Xero) {
                return texts.itemDeletedXero({
                    failureType: failureTypeText,
                    accountingName,
                    param: param1
                });
            }
            return texts.itemInactive({
                failureType: failureTypeText,
                accountingName,
                param: param1
            }, {
                readMore: readMoreLink(helpArticle.reactivateItems)
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrClassDeleted:
            return texts.classInactive({
                failureType: failureTypeText,
                accountingName,
                param: param1
            }, {
                readMore: readMoreLink(helpArticle.reactivateItems)
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrInvoiceAmountNotMatching:
            return texts.invoiceAmountNotMatching({
                failureType: failureTypeText,
                accountingName
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrObjectNotFound:
            return texts.objectNotFound({
                failureType: failureTypeText,
                accountingName
            }, {
                readMore: readMoreLink(helpArticle.reactivateItems)
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrAccountDeleted:
            if (accounting === Accounting.Xero) {
                return texts.accountArchivedXero({
                    failureType: failureTypeText,
                    accountingName,
                    param: param1
                }, {
                    readMore: readMoreLink(helpArticle.reactivateItems)
                });
            }
            return texts.accountInactive({
                failureType: failureTypeText,
                accountingName,
                param: param1
            }, {
                readMore: readMoreLink(helpArticle.reactivateItems)
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrVendorDeleted:
            if (accounting === Accounting.Xero) {
                return texts.vendorArchivedXero({
                    failureType: failureTypeText,
                    accountingName,
                    param: param1
                }, {
                    readMore: readMoreLink(helpArticle.reactivateItems)
                });
            }
            return texts.vendorInactive({
                failureType: failureTypeText,
                accountingName,
                param: param1
            }, {
                readMore: readMoreLink(helpArticle.reactivateItems)
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrInvoicePaid:
            return texts.invoicePaid({
                failureType: failureTypeText,
                accountingName
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrDepositTotalAmountNotMatching:
            return texts.depositTotalAmountNotMatching({
                failureType: failureTypeText,
                accountingName
            }, {
                contactSupport
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrDepositBankAccountInvalid:
            return texts.depositBankAccountInvalid({
                failureType: failureTypeText,
                accountingName
            }, {
                readMore: readMoreLink(helpArticle.setup)
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrBusinessValidationTransactionCannotBeApplied:
            {
                const paramLink = failure.paramLink();
                const depositUrl = paramLink === null || paramLink === void 0 ? void 0 : paramLink.url;
                return texts.transactionCannotBeApplied({
                    failureType: failureTypeText,
                    accountingName,
                    param: param1,
                    count: param1.split(', ').length
                }, {
                    deposit: depositUrl ? /*#__PURE__*/ _jsx(ExternalLinkButton, {
                        openInNewTab: true,
                        testId: AccountingSyncErrorMessageTestIds.DepositLink(),
                        href: depositUrl,
                        bi: {
                            name: 'accounting_error_deposit_link'
                        }
                    }) : /*#__PURE__*/ _jsx(_Fragment, {}),
                    readMore: readMoreLink(helpArticle.unlinkPayment)
                });
            }
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrInvoiceDeleted:
            return texts.invoiceDeleted({
                failureType: failureTypeText,
                failureTypeLowercase: translatedFailureTypeLowercase(failureType, texts),
                accountingName
            }, {
                contactSupport
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrPaymentDeleted:
            return accounting === Accounting.QBO ? texts.paymentDeletedResyncBoth({
                failureType: failureTypeText,
                accountingName,
                param: param1
            }, {
                contactSupport
            }) : texts.paymentDeletedResyncPayment({
                failureType: failureTypeText,
                accountingName,
                param: param1
            }, {
                contactSupport
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrAccountPeriodClosed:
            return texts.accountPeriodClosed({
                failureType: failureTypeText,
                accountingName
            }, {
                readMore: readMoreLink(helpArticle.accountPeriodClosed)
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrInvalidCompanyStatus:
            return texts.invalidCompanyStatus({
                failureType: failureTypeText,
                accountingName
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrMissingPermissions:
            return texts.missingPermissions({
                failureType: failureTypeText,
                accountingName
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.ClassTrackingDisabledWithOverride:
            return texts.classTrackingDisabledWithOverride({
                accountingName
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrItemIsCategory:
            return texts.itemIsCategory({
                failureType: failureTypeText,
                accountingName,
                param: param1
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrCodeInvoiceClientNotMapped:
            return texts.clientNotMapped({
                failureType: failureTypeText,
                accountingName
            }, {
                readMore: readMoreLink(helpArticle.contactMapping)
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrCodeInvoiceServiceNotMapped:
            return texts.serviceNotMapped({
                failureType: failureTypeText,
                accountingName
            }, {
                readMore: readMoreLink(helpArticle.servicesMapping)
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrCodeInvoiceClientAndServiceNotMapped:
            return texts.clientAndServiceNotMapped({
                failureType: failureTypeText,
                accountingName
            }, {
                readMore: readMoreLink(helpArticle.setup)
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrCodePayoutTransactionsNotSynced:
            {
                const invoicesToSync = failure.invoicesToSync();
                const paymentsToSync = failure.paymentsToSync();
                const useInvoicesToSync = invoicesToSync.length > 0;
                const itemsToSyncText = (useInvoicesToSync ? invoicesToSync : paymentsToSync).map((inv)=>inv.formattedNumber).join(', ');
                return texts.payoutTransactionsNotSynced({
                    failureType: failureTypeText,
                    itemType: useInvoicesToSync ? 'invoices' : 'payments',
                    accountingName,
                    items: itemsToSyncText
                });
            }
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrTimeActivityCannotAppliedToInvoice:
            return texts.timeActivityCannotBeApplied({
                failureType: failureTypeText,
                accountingName
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrInvoiceCreationLockDate:
            return texts.invoiceCreationLockDate({
                failureType: failureTypeText,
                accountingName
            }, {
                readMore: readMoreLink(helpArticles.xeroCreationLocked())
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.RequestTimeout:
            return texts.requestTimeout({
                failureType: failureTypeText
            }, {
                contactSupport
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrRateLimitExceeded:
            return texts.rateLimitExceeded({
                failureType: failureTypeText,
                accountingName
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.RefundAmountNotMatching:
            return texts.refundAmountNotMatching({
                failureType: failureTypeText,
                accountingName
            }, {
                contactSupport
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.PerLineRefundRejected:
            return texts.perLineRefundRejected({
                failureType: failureTypeText,
                accountingName
            }, {
                contactSupport
            });
        default:
            return renderUnclassifiedFailure({
                failure,
                failureType,
                accounting,
                texts
            });
    }
}
function renderUnclassifiedFailure(param) {
    let { failure, failureType, accounting, texts } = param;
    const detail = failure.rawDetail();
    const accountingName = getAccountingDetails(accounting).fullName;
    const failureTypeText = translatedFailureType(failureType, texts);
    if (detail !== undefined) {
        return texts.unclassifiedWithDetail({
            failureType: failureTypeText,
            accountingName,
            detail
        }, {
            contactSupport
        });
    }
    return texts.unclassified({
        failureType: failureTypeText,
        accountingName
    }, {
        contactSupport
    });
}
